import {
  FileText,
  Image,
  Video,
  Music,
  File,
  FileSpreadsheet,
  FileCode,
  FileArchive,
  FilePieChart,
  FileQuestion,
} from 'lucide-react';

export interface FileTypeInfo {
  icon: JSX.Element;
  category: 'pdf' | 'spreadsheet' | 'document' | 'code' | 'archive' | 'presentation' | 'image' | 'video' | 'audio' | 'other';
  color: string;
}

/**
 * Get icon, category, and color based on file type
 * @param fileType - The file type/extension (e.g., 'pdf', 'jpg', 'application/pdf')
 * @param size - Icon size class (default: 'w-5 h-5')
 * @returns FileTypeInfo object with icon, category, and color
 */
export function getFileTypeInfo(fileType: string, size: string = 'w-5 h-5'): FileTypeInfo {
  try {
    // Fallback for null, undefined, or empty string
    if (!fileType || fileType.trim() === '') {
      return {
        icon: <FileQuestion className={`${size} text-gray-400`} />,
        category: 'other',
        color: 'text-gray-400',
      };
    }

    const type = fileType.toLowerCase().trim();

  // PDF files
  if (type === 'pdf' || type === 'application/pdf') {
    return {
      icon: <FileText className={`${size} text-red-500`} />,
      category: 'pdf',
      color: 'text-red-500',
    };
  }

  // Spreadsheet files
  if (
    type === 'csv' ||
    type === 'xlsx' ||
    type === 'xls' ||
    type.includes('spreadsheet') ||
    type.includes('excel')
  ) {
    return {
      icon: <FileSpreadsheet className={`${size} text-green-600`} />,
      category: 'spreadsheet',
      color: 'text-green-600',
    };
  }

  // Word/Document files
  if (
    type === 'doc' ||
    type === 'docx' ||
    type === 'txt' ||
    type === 'text/plain' ||
    type.includes('word') ||
    type.includes('document')
  ) {
    return {
      icon: <FileText className={`${size} text-blue-600`} />,
      category: 'document',
      color: 'text-blue-600',
    };
  }

  // Code files
  if (
    type === 'js' ||
    type === 'ts' ||
    type === 'jsx' ||
    type === 'tsx' ||
    type === 'py' ||
    type === 'java' ||
    type === 'cpp' ||
    type === 'c' ||
    type === 'h' ||
    type === 'cs' ||
    type === 'php' ||
    type === 'rb' ||
    type === 'go' ||
    type === 'rs' ||
    type === 'swift' ||
    type === 'kt' ||
    type === 'html' ||
    type === 'css' ||
    type === 'scss' ||
    type === 'json' ||
    type === 'xml' ||
    type === 'yaml' ||
    type === 'yml' ||
    type === 'sql'
  ) {
    return {
      icon: <FileCode className={`${size} text-purple-600`} />,
      category: 'code',
      color: 'text-purple-600',
    };
  }

  // Archive files
  if (
    type === 'zip' ||
    type === 'rar' ||
    type === 'tar' ||
    type === 'gz' ||
    type === '7z' ||
    type === 'bz2' ||
    type === 'xz'
  ) {
    return {
      icon: <FileArchive className={`${size} text-orange-600`} />,
      category: 'archive',
      color: 'text-orange-600',
    };
  }

  // Presentation files
  if (
    type === 'ppt' ||
    type === 'pptx' ||
    type.includes('presentation')
  ) {
    return {
      icon: <FilePieChart className={`${size} text-orange-500`} />,
      category: 'presentation',
      color: 'text-orange-500',
    };
  }

  // Image files
  if (
    type === 'jpg' ||
    type === 'jpeg' ||
    type === 'png' ||
    type === 'gif' ||
    type === 'svg' ||
    type === 'webp' ||
    type === 'bmp' ||
    type === 'ico' ||
    type.includes('image')
  ) {
    return {
      icon: <Image className={`${size} text-blue-500`} />,
      category: 'image',
      color: 'text-blue-500',
    };
  }

  // Video files
  if (
    type === 'mp4' ||
    type === 'avi' ||
    type === 'mov' ||
    type === 'wmv' ||
    type === 'webm' ||
    type === 'mkv' ||
    type === 'flv' ||
    type === 'm4v' ||
    type.includes('video')
  ) {
    return {
      icon: <Video className={`${size} text-purple-500`} />,
      category: 'video',
      color: 'text-purple-500',
    };
  }

  // Audio files
  if (
    type === 'mp3' ||
    type === 'wav' ||
    type === 'm4a' ||
    type === 'ogg' ||
    type === 'flac' ||
    type === 'aac' ||
    type === 'wma' ||
    type.includes('audio')
  ) {
    return {
      icon: <Music className={`${size} text-pink-500`} />,
      category: 'audio',
      color: 'text-pink-500',
    };
  }

    // Default fallback - unknown/unrecognized file type
    // Use FileQuestion icon to indicate unknown type
    return {
      icon: <FileQuestion className={`${size} text-gray-400`} />,
      category: 'other',
      color: 'text-gray-400',
    };
  } catch (error) {
    // Error handling fallback - if anything goes wrong, return a safe default
    console.error('Error in getFileTypeInfo:', error);
    return {
      icon: <FileQuestion className={`${size} text-gray-400`} />,
      category: 'other',
      color: 'text-gray-400',
    };
  }
}

/**
 * Get display name for file type
 * @param fileType - The file type/extension
 * @returns Uppercase display name (e.g., 'PDF', 'JPG', 'DOCX')
 */
export function getFileTypeDisplayName(fileType: string): string {
  if (!fileType) return 'UNKNOWN';

  // If it's a MIME type, convert to extension for display
  if (fileType.includes('/')) {
    const parts = fileType.split('/');
    let ext = parts[1] || fileType;

    // Handle special MIME type mappings
    const mimeToExt: Record<string, string> = {
      'vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'vnd.ms-excel': 'XLS',
      'vnd.ms-powerpoint': 'PPT',
      'msword': 'DOC',
      'pdf': 'PDF',
      'plain': 'TXT',
      'jpeg': 'JPG',
      'quicktime': 'MOV',
      'x-msvideo': 'AVI',
      'mpeg': 'MP3',
      'x-wav': 'WAV',
      'csv': 'CSV',
    };

    return mimeToExt[ext] || ext.toUpperCase();
  }

  // Already an extension, just uppercase it
  return fileType.toUpperCase();
}
